import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { Code, Bot, Zap, Shield, Database, ArrowRight, ChevronUp, Calendar, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';

// 3D scene is heavy — load it lazily so it never blocks first paint.
const TradingTerminal3D = lazy(() => import('../components/TradingTerminal3D'));

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Don't ship WebGL to low-power / small devices — show a static gradient instead.
const canRender3D = () => {
  if (typeof window === 'undefined') return false;
  if (prefersReducedMotion()) return false;
  if (window.innerWidth < 768) return false;
  try {
    const c = document.createElement('canvas');
    return !!(c.getContext('webgl2') || c.getContext('webgl'));
  } catch {
    return false;
  }
};

const Home = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [use3D, setUse3D] = useState(false);
  const scrollProgress = useRef(0);

  useEffect(() => {
    setUse3D(canRender3D());

    const handleScroll = () => {
      const y = window.scrollY;
      scrollProgress.current = Math.min(y / (window.innerHeight || 1), 1);
      setShowScrollTop(y > (window.innerHeight || 0) * 0.8);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <div className="relative bg-slate-900 text-white">
      {/* ===== HERO ===== */}
      <section className="relative h-screen flex flex-col justify-center overflow-hidden">
        {/* 3D layer */}
        <div className="absolute inset-0 z-0">
          {use3D ? (
            <Suspense fallback={<HeroBackdrop />}>
              <TradingTerminal3D scrollProgress={scrollProgress} />
            </Suspense>
          ) : (
            <HeroBackdrop />
          )}
        </div>

        {/* gradient scrims for text legibility */}
        <div className="absolute inset-0 z-10 bg-gradient-to-b from-slate-900/70 via-slate-900/20 to-slate-900 pointer-events-none" />

        <div className="container-width relative z-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-4xl mx-auto"
          >
            <span className="inline-block mb-6 px-4 py-1.5 rounded-full border border-blue-400/40 bg-blue-500/10 text-blue-300 text-sm font-medium backdrop-blur-sm">
              Algorithmic Trading · Automation · Bots
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.05]">
              Automation That Moves at the
              <span className="gradient-text block mt-2">Speed of Thought</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
              Building automation that reacts faster, executes better, and never sleeps.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                className="px-8 py-3.5 bg-blue-500 hover:bg-blue-600 rounded-lg text-lg font-semibold transition-all hover:scale-105 hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] flex items-center group"
              >
                Get Started
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <Link
                to="/pricing"
                className="px-8 py-3.5 bg-white/10 hover:bg-white/20 rounded-lg text-lg font-semibold backdrop-blur-sm transition border border-white/10"
              >
                View Pricing
              </Link>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 text-white/70">
          <ChevronDown className="w-8 h-8 animate-bounce" />
        </div>
      </section>

      {/* ===== STATS ===== */}
      <section className="relative z-20 bg-slate-950 border-y border-white/5 py-14">
        <div className="container-width grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <Stat value={24} suffix="/7" label="Always-on execution" />
          <Stat value={100} suffix="%" label="Custom-built solutions" />
          <Stat value={5} suffix="+" label="Automation domains" />
          <Stat value={0} suffix="ms" label="Hesitation. It never sleeps." prefix="~" />
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section className="relative z-20 bg-slate-900 py-24">
        <div className="container-width">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-4">Our Services</h2>
            <p className="text-slate-400 text-center max-w-2xl mx-auto mb-14">
              Engineered systems for traders and businesses who need their software to act before the moment passes.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <ServiceCard {...s} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PROOF ===== */}
      <section className="relative z-20 bg-slate-950 py-24 overflow-hidden">
        <div className="container-width grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Find inefficiencies <span className="gradient-text">before they drain your account.</span>
              </h2>
              <p className="text-slate-400 mb-6 leading-relaxed">
                Every strategy is backtested and stress-tested against real market conditions — broker outages,
                volatility spikes, slippage — before a single rupee goes live. We build the edge; you keep it.
              </p>
              <ul className="space-y-3 text-slate-300">
                {['Backtesting & strategy validation', 'Secure broker API integration', 'Automated risk management'].map(
                  (item) => (
                    <li key={item} className="flex items-center gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                      {item}
                    </li>
                  ),
                )}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <EquityCard />
          </Reveal>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="relative z-20">
        <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white py-20">
          <div className="container-width text-center">
            <Reveal>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">Ready to Transform Your Business?</h2>
              <p className="text-lg mb-8 max-w-2xl mx-auto text-blue-50">
                Let's discuss how we can help you achieve your goals
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                <Link
                  to="/contact"
                  className="inline-block bg-white text-blue-600 px-8 py-3.5 rounded-lg text-lg font-semibold hover:bg-slate-100 transition transform hover:scale-105"
                >
                  Contact Us Today
                </Link>
                <a
                  href="https://calendly.com/visrupt/schedule-a-call"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3.5 bg-white/15 text-white rounded-xl font-semibold hover:bg-white/25 transition-all duration-300 group"
                >
                  <Calendar className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-300" />
                  <span className="group-hover:translate-x-1 transition-transform duration-300">Schedule a Call</span>
                </a>
              </div>
            </Reveal>
          </div>
        </div>
        <Footer />
      </section>

      <button
        onClick={scrollToTop}
        className={`fixed bottom-8 right-4 sm:right-8 z-40 bg-blue-500 text-white p-3 rounded-full shadow-lg transition-all duration-300 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 ${
          showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16 pointer-events-none'
        }`}
        aria-label="Scroll to top"
      >
        <ChevronUp className="w-6 h-6" />
      </button>
    </div>
  );
};

/* ---------- subcomponents ---------- */

const HeroBackdrop = () => (
  <div className="absolute inset-0 bg-slate-900">
    <div className="absolute inset-0 bg-gradient-to-br from-blue-600/25 via-slate-900 to-slate-900" />
    <div
      className="absolute inset-0 opacity-[0.15] animate-grid-pan"
      style={{
        backgroundImage:
          'linear-gradient(#3b82f6 1px, transparent 1px), linear-gradient(90deg, #3b82f6 1px, transparent 1px)',
        backgroundSize: '40px 40px',
      }}
    />
  </div>
);

const SERVICES = [
  { icon: <Zap />, title: 'Custom Trading Algorithms', description: 'High-performance, rule-based trading strategies with comprehensive backtesting capabilities' },
  { icon: <Shield />, title: 'Trading Infrastructure', description: 'Secure API integration with brokers and automated risk management solutions' },
  { icon: <Code />, title: 'Python Automation', description: 'Custom automation tools to streamline workflows and eliminate repetitive tasks' },
  { icon: <Bot />, title: 'Chatbot Development', description: 'AI-driven Telegram & Discord bots for customer support and community management' },
  { icon: <Database />, title: 'Game Bot Solutions', description: 'Custom Discord game bots to enhance user engagement and automate functionalities' },
  { icon: <ArrowRight />, title: 'View All Services', description: 'Explore our complete range of services and solutions', link: '/services', isLink: true },
];

const ServiceCard = ({ icon, title, description, link, isLink }: any) => {
  const ref = useRef<HTMLDivElement>(null);

  const onMove = (e: React.MouseEvent) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(800px) rotateX(${-py * 6}deg) rotateY(${px * 6}deg) translateY(-4px)`;
  };
  const reset = () => {
    if (ref.current) ref.current.style.transform = '';
  };

  const inner = (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={reset}
      className="h-full bg-slate-800/60 backdrop-blur-sm border border-white/5 p-6 rounded-xl transition-all duration-200 hover:border-blue-400/40 hover:shadow-[0_0_30px_rgba(59,130,246,0.15)]"
    >
      <div className="text-blue-400 mb-4">{React.cloneElement(icon, { className: 'w-9 h-9' })}</div>
      <h3 className="text-lg font-semibold mb-2">{title}</h3>
      <p className="text-slate-400 text-sm leading-relaxed">{description}</p>
      {isLink && (
        <span className="inline-flex items-center gap-1 mt-4 text-blue-400 text-sm font-medium">
          Explore <ArrowRight className="w-4 h-4" />
        </span>
      )}
    </div>
  );

  return isLink ? (
    <Link to={link} className="block h-full">
      {inner}
    </Link>
  ) : (
    inner
  );
};

const Stat = ({ value, suffix = '', prefix = '', label }: { value: number; suffix?: string; prefix?: string; label: string }) => {
  const [n, setN] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        obs.disconnect();
        const start = performance.now();
        const dur = 1200;
        const tick = (t: number) => {
          const p = Math.min((t - start) / dur, 1);
          setN(Math.round((1 - Math.pow(1 - p, 3)) * value));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.4 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [value]);

  return (
    <div ref={ref}>
      <div className="text-4xl sm:text-5xl font-bold gradient-text">
        {prefix}
        {n}
        {suffix}
      </div>
      <div className="text-slate-400 text-sm mt-2">{label}</div>
    </div>
  );
};

/** Animated SVG equity curve that draws itself when scrolled into view. */
const EquityCard = () => (
  <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 shadow-2xl">
    <div className="flex items-center justify-between mb-4">
      <span className="text-sm text-slate-400">Backtest · Equity Curve</span>
      <span className="text-green-400 text-sm font-semibold">+payoff</span>
    </div>
    <svg viewBox="0 0 400 200" className="w-full">
      <defs>
        <linearGradient id="eq" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#22c55e" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[40, 80, 120, 160].map((y) => (
        <line key={y} x1="0" y1={y} x2="400" y2={y} stroke="#1e293b" strokeWidth="1" />
      ))}
      <motion.path
        d="M0 180 C 60 170, 90 120, 140 130 S 220 60, 280 70 S 360 20, 400 25"
        fill="none"
        stroke="#22c55e"
        strokeWidth="3"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.8, ease: 'easeInOut' }}
      />
      <motion.path
        d="M0 180 C 60 170, 90 120, 140 130 S 220 60, 280 70 S 360 20, 400 25 L 400 200 L 0 200 Z"
        fill="url(#eq)"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.5, delay: 0.6 }}
      />
    </svg>
  </div>
);

export default Home;
